import { maskitoParseDate } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { TextField, TextFieldProps } from "../../TextField";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMergeRefs } from "@floating-ui/react";
import { DateTime } from "luxon";
import datePlaceholderMask from "./maskito/datePlaceholderMask";
import { Icon } from "../../Icon";
import Event from "@servicetitan/hammer-icon/mdi/round/event.svg";
import { DateModeToFormatMap, DateModeToPlaceholderMap } from "./constants";
import { DateMode } from "../types";
import { usePrevious } from "../../../internal";

export type MaskedDateInputChange = {
  date: DateTime | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
};

export type MaskedDateInputChangeHandler = (
  change: MaskedDateInputChange,
) => void;

export type MaskedDateInputProps = Omit<
  TextFieldProps,
  "onChange" | "value" | "ref" | "showCounter" | "hint" | "prefix"
> & {
  onChange: MaskedDateInputChangeHandler;
  mode?: DateMode;
  lastValidDate: DateTime | null;
  disableHint?: boolean;
};

export type MaskedDateInputRef = HTMLInputElement & {
  setDate: (date: DateTime | null) => void;
};

/**
 * A masked date input component that allows for the input of a date in a specific format.
 *
 * Provides a `setDate` method that can be used to set the date from outside the component (e.g from a calendar).
 *
 * @internal This component is not intended to be used directly in consumer code.
 *
 * @param props - The props for the MaskedDateInput component.
 * @param props.onChange - The function to call when the date changes.
 * @param props.mode - The mode of the date input.
 */
export const MaskedDateInput = forwardRef<
  MaskedDateInputRef,
  MaskedDateInputProps
>(
  (
    {
      onChange,
      mode = "mm/dd/yyyy",
      lastValidDate,
      disableHint = false,
      ...props
    },
    ref,
  ) => {
    const placeholder = DateModeToPlaceholderMap[mode];
    const [inputValue, setInputValue] = useState(placeholder);
    const { options, removePlaceholder } = datePlaceholderMask({
      mode,
      placeholder,
    });
    const maskedInputRef = useMaskito({ options });
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useMergeRefs([maskedInputRef, inputRef, ref]);
    const previousDateRef = useRef<DateTime | null>(null);
    const previousMode = usePrevious(mode);

    // Update the input value when the mode changes.
    useEffect(() => {
      if (mode !== previousMode) {
        setInputValue((oldInputValue) =>
          swapMode(oldInputValue, previousMode ?? mode, mode),
        );
      }
    }, [mode, previousMode]);

    // Update the input value when the parent's date changes.
    useEffect(() => {
      if (lastValidDate === undefined) return;
      if (lastValidDate === previousDateRef.current) return; // Note: Reference equality is intentional here
      if (!lastValidDate?.equals(previousDateRef.current ?? DateTime.now())) {
        setInputValue(
          lastValidDate?.toFormat(DateModeToFormatMap[mode]) ?? placeholder,
        );
        previousDateRef.current = lastValidDate;
      }
    }, [lastValidDate, mode, placeholder]);

    const currentParsedData = useMemo(() => {
      return parseInputValue(inputValue, mode, removePlaceholder);
    }, [inputValue, mode, removePlaceholder]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
      const { date, isInputValid, isInputEmpty } = parseInputValue(
        event.target.value,
        mode,
        removePlaceholder,
      );

      // We only want to call onChange for substantive changes.
      // This is to avoid unnecessary re-renders of upstream components.
      const isValueDifferent =
        isInputValid !== currentParsedData.isInputValid || // The input has changed validity
        isInputEmpty !== currentParsedData.isInputEmpty || // The input has changed emptiness
        (date === null) !== (lastValidDate === null) || // The input has changed from empty to non-empty or vice versa
        (date !== null &&
          lastValidDate !== null &&
          !date.equals(lastValidDate)); // The date has changed

      // If the value has not changed, we do not need to call onChange.
      if (!isValueDifferent) return;
      onChange?.({
        date: date ?? lastValidDate ?? null,
        isInputValid,
        isInputEmpty,
      });
    };

    // Extend the input ref with additional methods.
    // This provides a way to set the input value from outside the component.
    useImperativeHandle(ref, () => {
      const input = inputRef.current;
      if (!input) return null as unknown as MaskedDateInputRef;
      return Object.assign(input, {
        setDate: (date: DateTime | null) => {
          setInputValue(
            date?.toFormat(DateModeToFormatMap[mode]) ?? placeholder,
          );
        },
      });
    }, [mode, placeholder]);

    return (
      <TextField
        ref={combinedRef}
        data-date={lastValidDate?.toISODate() ?? ""}
        data-input-valid={currentParsedData.isInputValid}
        data-input-empty={currentParsedData.isInputEmpty}
        {...props}
        showCounter={false}
        value={inputValue}
        onChange={handleChange}
        prefix={<Icon svg={Event} />}
        hint={disableHint ? undefined : `Format: ${mode}`}
      />
    );
  },
);

MaskedDateInput.displayName = "MaskedDateInput";

function parseInputValue(
  value: string,
  mode: DateMode,
  removePlaceholder: (value: string) => string,
) {
  const valueMinusPlaceholder = removePlaceholder(value);
  const jsDate = maskitoParseDate(valueMinusPlaceholder, { mode });
  const luxonDate = jsDate
    ? DateTime.fromJSDate(jsDate, { zone: "utc" })
    : null;
  return {
    date: luxonDate,
    isInputValid: !!luxonDate,
    isInputEmpty: valueMinusPlaceholder === "",
  };
}

function swapMode(inputString: string, previousMode: DateMode, mode: DateMode) {
  const { day, month, year } = divideSegments(inputString, previousMode);
  return orderSegmentsByMode(day, month, year, mode);
}

function divideSegments(
  value: string,
  mode: DateMode,
): {
  day: string;
  month: string;
  year: string;
} {
  const [segment1, segment2, segment3] = value.split("/");
  if (mode === "dd/mm/yyyy") {
    return { day: segment1, month: segment2, year: segment3 };
  } else if (mode === "mm/dd/yyyy") {
    return { day: segment2, month: segment1, year: segment3 };
  } else if (mode === "yyyy/mm/dd") {
    return { day: segment3, month: segment2, year: segment1 };
  }
  return { day: "__", month: "__", year: "____" };
}

function orderSegmentsByMode(
  day: string,
  month: string,
  year: string,
  mode: DateMode,
) {
  if (mode === "dd/mm/yyyy") {
    return `${day}/${month}/${year}`;
  } else if (mode === "mm/dd/yyyy") {
    return `${month}/${day}/${year}`;
  } else if (mode === "yyyy/mm/dd") {
    return `${year}/${month}/${day}`;
  } else {
    return "";
  }
}
